const PERSONALIZER_ALLOWED_CHARACTERS = /[^a-zA-Z0-9&+=%♥]/g;
const PERSONALIZER_HEART = "♥";

// Multiplies font_combination_settings' authored (negative) spacing value - e.g. -8px becomes -12px -
// so characters sit closer together than the metafield's raw value alone would produce.
const PERSONALIZER_LETTER_SPACING_FACTOR = 1.5;
const PERSONALIZER_ALLOWED_CHARACTERS_WITH_SPACE = /[^a-zA-Z0-9&+=%♥ ]/g;
const PERSONALIZER_ALLOWED_CHARACTERS_LETTERS_ONLY = /[^a-zA-Z]/g;
const PERSONALIZER_ALLOWED_CHARACTERS_LETTERS_ONLY_WITH_SPACE = /[^a-zA-Z ]/g;


// None of the three custom stamping fonts (gothic-lower/gothic-upper/thick-block) have a heart glyph -
// confirmed directly from their cmap tables, which only define 0xe900-0xe928. So the heart is rendered as
// this inline SVG instead of font text (same approach already used on the live site), with a drop-shadow
// filter matching the .char-icon text-shadow so it reads as etched like the surrounding letters.
const PERSONALIZER_HEART_ICON_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style="display:block;width:100%;height:100%;"><path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" style="filter: drop-shadow(rgba(255, 255, 255, 0.3) 1px 1px 2px) drop-shadow(rgba(0, 0, 0, 0.5) -1px -1px 2px);"></path></svg>';

// The heart icon has no natural glyph advance width to size itself by, so its box is sized explicitly
// instead, from customizer.heart_icon_size (see PersonalizerSteps.getHeartIconSize()) rather than scaled
// off the current font-size. Used whenever that metafield is blank, or doesn't cover the current
// orientation (e.g. it only ever defines "sideways"/"vertical", never the default horizontal layout).
const PERSONALIZER_HEART_ICON_DEFAULT_SIZE = 42;

const PERSONALIZER_SPACE_WIDTH_FACTOR = 0.1;

// customizer.stampings_spec's per-length "orientation" field (distinct from the reading orientation - see
// this.selectedOrientation) can be "square" for a given character count, which wraps that row's characters
// into a roughly-square grid instead of one flat line - e.g. 3 characters becomes a 2-then-1 pyramid,
// matching the live site. Each spec entry can also carry its own "rows" (chars per chunk) and "row_gap"
// (see PERSONALIZER_ROW_GAP_FACTOR below) - these two constants are only the fallback used when an entry
// is missing that field, or missing entirely for a given character count.
const PERSONALIZER_SQUARE_ROW_MAX_CHARS = 2;
const PERSONALIZER_ROW_GAP_FACTOR = -0.15;

const PERSONALIZER_FONT_FAMILIES = {
  "Gothic Lowercase": "Gothic Lower Stamping",
  "Gothic Uppercase": "Gothic Upper Stamping",
  "Minimal Block": "Thick Block Stamping"
};

class PersonalizerSteps extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.backButton = this.parentElement.querySelector("[data-personalizer-prev]");
    const { signal } = this.abortController;

    this.addEventListener(
      "click",
      (event) => {
        const nextTrigger = event.target.closest("[data-personalizer-next]");
        if (nextTrigger) {
          const currentStep = nextTrigger.closest("[data-personalizer-step]");
          this.goToStep(currentStep, currentStep?.nextElementSibling);
          return;
        }

        const option = event.target.closest("[data-personalizer-option]");
        if (option) {
          this.selectOption(option);
          return;
        }

        const heartTrigger = event.target.closest("[data-personalizer-insert-heart]");
        if (heartTrigger) {
          this.insertHeart(heartTrigger);
        }
      },
      { signal }
    );

    this.addEventListener(
      "input",
      (event) => {
        if (event.target.matches("[data-personalizer-text-input]")) {
          this.onTextInput(event.target);
        }
      },
      { signal }
    );

    this.backButton?.addEventListener(
      "click",
      () => {
        const currentStep = this.querySelector("[data-personalizer-step]:not([hidden])");
        this.goToStep(currentStep, currentStep?.previousElementSibling);
      },
      { signal }
    );

    if (this.dataset.lettersOptionPosition) {
      document.addEventListener("change", (event) => this.onLettersOptionChange(event), { signal });
    }

    // Liquid emits data-base-font-size/data-base-letter-spacing directly for the default font, so
    // fitPreviewText() normally has something to reset to and scale from even if the customer never opens
    // the font step. Fall back to parsing the inline style Liquid also writes, only if those attributes
    // are missing for some reason (e.g. an older cached section render).
    const previewTextArea = this.querySelector("[data-personalizer-preview-text-area]");
    if (previewTextArea && previewTextArea.dataset.baseFontSize === undefined) {
      const initialFontSize = parseFloat(previewTextArea.style.fontSize);
      const initialLetterSpacing = parseFloat(previewTextArea.style.letterSpacing);

      if (!Number.isNaN(initialFontSize)) {
        previewTextArea.dataset.baseFontSize = initialFontSize;
      }

      if (!Number.isNaN(initialLetterSpacing)) {
        previewTextArea.dataset.baseLetterSpacing = initialLetterSpacing;
      }
    }

    // Same idea for the font-family lookup in applyPreviewStyle(): without this, typing before ever opening
    // the font step leaves this.selectedFont undefined, so the default font's family never resolves and the
    // preview silently falls back to the generic heading font instead of the correct stamping font.
    if (previewTextArea?.dataset.baseFontLabel) {
      this.selectedFont = previewTextArea.dataset.baseFontLabel;
    }

    // this.selectedFontHandle (distinct from this.selectedFont, which is the display label) is what
    // getFontSettings() matches against customizer.font_combination_settings' "font" key - needed as soon as
    // orientation changes so the currently-selected font's size/spacing can be recomputed for it (see
    // selectOption()'s orientation branch below).
    if (previewTextArea?.dataset.baseFontHandle) {
      this.selectedFontHandle = previewTextArea.dataset.baseFontHandle;
    }

    // Same idea again, for orientation: without this, typing before ever opening the orientation step leaves
    // this.selectedOrientation undefined, so "Vertical" products would render horizontally by default.
    if (previewTextArea?.dataset.baseOrientation) {
      this.selectedOrientation = previewTextArea.dataset.baseOrientation;
    }

    // customizer.stampings_spec (see personalizer-drawer.liquid) drives the "square" grid-wrapping layout -
    // keyed by character count, not by font, so it's parsed once here rather than re-parsed on every
    // keystroke. Invalid/missing JSON just means no rows ever match, i.e. no wrapping - never a hard error.
    this.stampingsSpec = [];
    if (previewTextArea?.dataset.stampingsSpec) {
      try {
        this.stampingsSpec = JSON.parse(previewTextArea.dataset.stampingsSpec);
      } catch (error) {
        this.stampingsSpec = [];
      }
    }

    // customizer.heart_icon_size (see personalizer-drawer.liquid) drives the heart icon's rendered size,
    // keyed by orientation - e.g. {"sideways":42,"vertical":42} - looked up per-render in getHeartIconSize()
    // since the orientation can change after the customer has already typed a heart. Invalid/missing JSON
    // just means every orientation falls back to PERSONALIZER_HEART_ICON_DEFAULT_SIZE - never a hard error.
    this.heartIconSize = {};
    if (previewTextArea?.dataset.heartIconSize) {
      try {
        this.heartIconSize = JSON.parse(previewTextArea.dataset.heartIconSize) || {};
      } catch (error) {
        this.heartIconSize = {};
      }
    }

    // customizer.font_combination_settings carries one entry per font+orientation pair (e.g. "thick_block"
    // has a separate row for "vertical" and for "sideways", each with its own fontSize/spacing) - parsed
    // once here and looked up dynamically in getFontSettings() as either the font or the orientation
    // selection changes, rather than baked into each font button at render time. Invalid/missing JSON just
    // means getFontSettings() never finds a match, i.e. font size/spacing stay whatever they already were -
    // never a hard error.
    this.fontCombinationSettings = [];
    if (previewTextArea?.dataset.fontCombinationSettings) {
      try {
        this.fontCombinationSettings = JSON.parse(previewTextArea.dataset.fontCombinationSettings);
      } catch (error) {
        this.fontCombinationSettings = [];
      }
    }

    // customizer.text_area_geometry.curve (see personalizer-drawer.liquid) - e.g. {"depthPercent":1.2} -
    // makes applyTextCurve() bow each character to follow the product photo's actual band curvature instead
    // of sitting on a flat line. Blank/invalid JSON just means no curve is applied (every product besides
    // Skinny Cuff today) - never a hard error.
    this.textCurve = null;
    if (previewTextArea?.dataset.textCurve) {
      try {
        this.textCurve = JSON.parse(previewTextArea.dataset.textCurve);
      } catch (error) {
        this.textCurve = null;
      }
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
  }

  onLettersOptionChange(event) {
    const input = event.target.closest?.("input[data-option-position]");
    if (!input || !input.checked || input.dataset.optionPosition !== this.dataset.lettersOptionPosition) {
      return;
    }

    const formId = input.getAttribute("form");
    const drawer = this.closest("x-drawer");
    if (!formId || !drawer || drawer.id !== `personalizer-drawer-${formId}`) {
      return;
    }

    const optionLabel = input.labels?.[0] || (input.nextElementSibling?.tagName === "LABEL" ? input.nextElementSibling : null);
    const newMax = optionLabel ? parseInt(optionLabel.textContent, 10) : NaN;
    if (Number.isNaN(newMax) || newMax <= 0) {
      return;
    }

    this.querySelectorAll("[data-personalizer-text-input]").forEach((textInput) => {
      textInput.value = "";
      textInput.setAttribute("maxlength", newMax);
    });

    this.querySelectorAll("[data-personalizer-char-counter]").forEach((counter) => {
      counter.textContent = counter.textContent.replace(/\d+\/\d+/, `0/${newMax}`);
    });

    const previewImage = this.querySelector(".personalizer-drawer__preview-wrapper img");
    if (previewImage?.dataset.originalSrc) {
      previewImage.src = previewImage.dataset.originalSrc;
      if (previewImage.dataset.originalSrcset) {
        previewImage.srcset = previewImage.dataset.originalSrcset;
      }
    }

    const addToCartButton = this.querySelector("[data-personalizer-add-to-cart-button]");
    if (addToCartButton) {
      addToCartButton.hidden = true;
    }

    const previewButton = this.querySelector("[data-personalizer-preview-button]");
    if (previewButton) {
      previewButton.hidden = false;
    }

    const statusEl = this.querySelector("[data-personalizer-preview-status]");
    if (statusEl) {
      statusEl.hidden = true;
      statusEl.textContent = "";
    }

    this.querySelectorAll("[data-personalizer-step]").forEach((step, index) => {
      step.hidden = index !== 0;
    });

    if (this.backButton) {
      this.backButton.hidden = true;
    }
  }

  // Matches on both font AND the current orientation - a plain font-only match would silently grab whichever
  // entry for that font happens to come first in customizer.font_combination_settings regardless of which
  // orientation is actually selected (the bug this method exists to avoid). Falls back to a font-only match
  // if no entry declares the current orientation at all (e.g. the default horizontal layout, which this
  // metafield never seems to define).
  getFontSettings(fontHandle) {
    if (!fontHandle) {
      return null;
    }

    const orientation = (this.selectedOrientation || "").toLowerCase();

    return (
      this.fontCombinationSettings.find(
        (combination) => combination.font === fontHandle && (combination.orientation || "").toLowerCase() === orientation
      ) || this.fontCombinationSettings.find((combination) => combination.font === fontHandle) || null
    );
  }

  goToStep(currentStep, nextStep) {
    if (!currentStep || !nextStep) {
      return;
    }

    currentStep.hidden = true;
    nextStep.hidden = false;

    if (this.backButton) {
      this.backButton.hidden = nextStep === this.querySelector("[data-personalizer-step]");
    }
  }

  selectOption(option) {
    const step = option.closest("[data-personalizer-step]");

    step.querySelectorAll("[data-personalizer-option]").forEach((sibling) => {
      const selected = sibling === option;
      sibling.setAttribute("aria-checked", selected ? "true" : "false");
      sibling.classList.toggle("button--outline", !selected);
    });

    const selectedValueInput = step.querySelector("[data-personalizer-selected-value]");
    if (selectedValueInput) {
      selectedValueInput.value = option.dataset.value;
    }

    // Font options carry data-text-transform (derived from the label, e.g. "Gothic Uppercase" -> uppercase)
    // and data-font-handle, used to look up this font's size/spacing for the *current* orientation via
    // getFontSettings() - not baked in at render time, since customizer.font_combination_settings defines a
    // separate fontSize/spacing per font+orientation pair. None of this changes the input's value - it only
    // affects how the engraving preview renders. this.selectedFont is only updated here (not for orientation
    // options, which share this same method but never carry data-text-transform) - it drives the
    // font-family lookup in applyPreviewStyle().
    if (option.dataset.textTransform) {
      this.selectedFont = option.dataset.value;
      this.selectedFontHandle = option.dataset.fontHandle;

      const previewTextArea = this.querySelector("[data-personalizer-preview-text-area]");
      if (previewTextArea) {
        previewTextArea.style.textTransform = option.dataset.textTransform;

        // Mirrored onto the DOM too (not just this.selectedFont/this.selectedFontHandle) since
        // connectedCallback() reads data-base-font-label/data-base-font-handle back out on reconnect -
        // without this, a re-render/reconnect would forget the customer's chosen font and silently fall
        // back to the default one.
        previewTextArea.dataset.baseFontLabel = option.dataset.value;
        previewTextArea.dataset.baseFontHandle = option.dataset.fontHandle;

        // These become the new "base" (100%-fit) values for this font - fitPreviewText() always resets to
        // them first before deciding whether the current text needs shrinking, so switching fonts never
        // carries over a shrink factor computed for the previous font's metrics.
        const fontSettings = this.getFontSettings(option.dataset.fontHandle);
        if (fontSettings) {
          previewTextArea.dataset.baseFontSize = fontSettings.fontSize;
          previewTextArea.dataset.baseLetterSpacing = fontSettings.spacing;
        }

        this.fitPreviewText(previewTextArea);
      }
    }

    // Orientation options don't affect font metrics directly, but customizer.font_combination_settings can
    // define a different fontSize/spacing for the already-selected font under the new orientation (e.g.
    // going back from the font step to change orientation) - so this.selectedFontHandle is re-resolved
    // against the new orientation here too, not just how the typed characters are laid out (see
    // renderPreviewChars) - "Vertical" stacks every character on its own line instead of side by side, which
    // means the preview has to be rebuilt from the input step's current values, not just re-styled in place.
    if (option.dataset.orientation) {
      this.selectedOrientation = option.dataset.value;

      // Mirrored onto the DOM too (not just this.selectedOrientation) since connectedCallback() reads
      // data-base-orientation back out on reconnect - without this, a re-render/reconnect would forget the
      // customer's choice and silently fall back to the default orientation.
      const previewTextArea = this.querySelector("[data-personalizer-preview-text-area]");
      if (previewTextArea) {
        previewTextArea.dataset.baseOrientation = option.dataset.value;

        const fontSettings = this.getFontSettings(this.selectedFontHandle);
        if (fontSettings) {
          previewTextArea.dataset.baseFontSize = fontSettings.fontSize;
          previewTextArea.dataset.baseLetterSpacing = fontSettings.spacing;
        }
      }

      this.refreshPreviewFromInputs();
    }

    // Brief delay so the selected/outline swap is visible before the drawer advances.
    window.setTimeout(() => this.goToStep(step, step.nextElementSibling), 200);
  }

  onTextInput(input) {
    // .replace()/.match() with a global-flag regex always scan from the start regardless of prior calls,
    // but .test() would mutate the shared regex's lastIndex and corrupt matching on later keystrokes - so
    // the "did anything change" check has to be a value comparison, not PERSONALIZER_ALLOWED_CHARACTERS.test().
    const allowSpaces = this.dataset.allowSpaces === "true";
    const allowLettersOnly = this.dataset.allowLettersOnly === "true";

    let allowedCharacters;
    if (allowLettersOnly && allowSpaces) {
      allowedCharacters = PERSONALIZER_ALLOWED_CHARACTERS_LETTERS_ONLY_WITH_SPACE;
    } else if (allowLettersOnly) {
      allowedCharacters = PERSONALIZER_ALLOWED_CHARACTERS_LETTERS_ONLY;
    } else if (allowSpaces) {
      allowedCharacters = PERSONALIZER_ALLOWED_CHARACTERS_WITH_SPACE;
    } else {
      allowedCharacters = PERSONALIZER_ALLOWED_CHARACTERS;
    }
    const sanitized = input.value.replace(allowedCharacters, "");

    if (sanitized !== input.value) {
      const caret = input.selectionStart;
      const removedBeforeCaret = (input.value.slice(0, caret).match(allowedCharacters) || []).length;

      input.value = sanitized;
      input.selectionStart = input.selectionEnd = caret - removedBeforeCaret;
    }

    this.updateCharCounter(input);
    this.refreshPreviewFromInputs();
  }

  updateCharCounter(input) {
    const counter = input.closest(".personalizer-drawer__input-row")?.querySelector("[data-personalizer-char-counter]");
    if (counter) {
      counter.textContent = counter.textContent.replace(/\d+(?=\/)/, input.value.length);
    }
  }

  // Mirrors every [data-personalizer-text-input] (there's only one input step, one geometry box, and one
  // preview area total - not per-step - so this reads from `this`, the whole <personalizer-steps> element,
  // rather than scoping to whichever step happens to be visible when it's called) into the
  // [data-personalizer-preview-text-area] overlay, rebuilt as one <span class="char-icon etch"> per character
  // (matching the live reference site's structure) rather than a plain text node - that's what makes
  // per-character margin-based spacing possible below. Also called when the orientation selection changes
  // (see selectOption()), since that changes how those characters are laid out, not just their style.
  refreshPreviewFromInputs() {
    const previewTextArea = this.querySelector("[data-personalizer-preview-text-area]");
    if (!previewTextArea) {
      return;
    }

    const lines = Array.from(this.querySelectorAll("[data-personalizer-text-input]")).map((textInput) => textInput.value);

    // Only products whose text_area_geometry.side_overflow metafield is set (currently just Skinny Cuff -
    // see personalizer-drawer.liquid) render these two extra boxes, and only they carry
    // data-inside-max-chars - every other product falls through to the original single-box
    // renderPreviewChars()/fitPreviewText() path below, completely unchanged.
    const leftOverflowArea = this.querySelector("[data-personalizer-preview-text-area-left]");
    const rightOverflowArea = this.querySelector("[data-personalizer-preview-text-area-right]");
    const insideMaxChars = parseInt(previewTextArea.dataset.insideMaxChars, 10);

    if (leftOverflowArea && rightOverflowArea && !Number.isNaN(insideMaxChars) && lines.length === 1) {
      this.renderSplitPreview(previewTextArea, leftOverflowArea, rightOverflowArea, lines[0], insideMaxChars);
      this.fitPreviewText(previewTextArea);

      // leftOverflowArea/rightOverflowArea never carry their own data-base-font-size/letter-spacing (only
      // the middle box does, from personalizer-drawer.liquid) - they're meant to always match it exactly,
      // so their sizing is copied from the middle box's already-resolved dataset rather than read from
      // their own (blank) dataset the way fitPreviewText() normally would.
      [leftOverflowArea, rightOverflowArea].forEach((zone) => {
        zone.dataset.baseFontSize = previewTextArea.dataset.baseFontSize;
        zone.dataset.baseLetterSpacing = previewTextArea.dataset.baseLetterSpacing;
        this.fitPreviewText(zone);
      });
      this.applyTextCurve(previewTextArea, [leftOverflowArea, rightOverflowArea]);
      return;
    }

    if (leftOverflowArea) {
      leftOverflowArea.textContent = "";
    }
    if (rightOverflowArea) {
      rightOverflowArea.textContent = "";
    }

    this.renderPreviewChars(previewTextArea, lines);
    this.fitPreviewText(previewTextArea);
    this.applyTextCurve(previewTextArea, []);
  }

  // customizer.text_area_geometry.curve (parsed into this.textCurve) bows each character to sit along the
  // same gentle arc the product photo's band actually has (measured directly from the image: the flat
  // face's top edge sags a few px lower at its own center than it does near the stones/tabs) - added as a
  // transform on top of appendPreviewChar()'s normal flex-flow layout, which still does all the horizontal
  // positioning (letter-spacing, space width, etc.) untouched.
  //
  // Only sideZones (the overflow text past each stone, toward the tapered ends) ever get curved -
  // previewTextArea (the middle, between-stones box) sits on the flat part of the band, so it's always
  // reset flat here regardless of this.textCurve, never included in the curve math below.
  //
  // Modeled as a parabola across the whole preview wrapper's width: offset(x) = depthPx * (1 - t^2), where
  // t is -1..1 from the wrapper's left/right edge to its center - 0 (no vertical shift, no rotation) at
  // t=0 (i.e. this.textCurve blank/zero), maximum at the wrapper's own horizontal center. "direction":
  // "arch" flips the sign so the center sits higher instead of lower, for a product whose photo bows the
  // other way.
  applyTextCurve(previewTextArea, sideZones) {
    Array.from(previewTextArea.querySelectorAll(".char-icon")).forEach((charElement) => {
      charElement.style.transform = "";
    });

    const depthPercent = this.textCurve?.depthPercent;

    if (!depthPercent || sideZones.length === 0) {
      sideZones.forEach((zone) => {
        Array.from(zone.querySelectorAll(".char-icon")).forEach((charElement) => {
          charElement.style.transform = "";
        });
      });
      return;
    }

    const wrapper = previewTextArea.closest(".personalizer-drawer__preview-wrapper");
    const wrapperRect = wrapper?.getBoundingClientRect();
    if (!wrapperRect || !wrapperRect.width || !wrapperRect.height) {
      return;
    }

    const centerX = wrapperRect.left + wrapperRect.width / 2;
    const halfSpan = wrapperRect.width / 2;
    const depthPx = (depthPercent / 100) * wrapperRect.height;
    const sign = this.textCurve?.direction === "arch" ? -1 : 1;

    sideZones.forEach((zone) => {
      Array.from(zone.querySelectorAll(".char-icon")).forEach((charElement) => {
        const rect = charElement.getBoundingClientRect();
        const t = (rect.left + rect.width / 2 - centerX) / halfSpan;
        const offset = sign * depthPx * (1 - t * t);
        const slope = (sign * -2 * depthPx * t) / halfSpan;
        const angleDeg = Math.atan(slope) * (180 / Math.PI);
        charElement.style.transform = `translateY(${offset.toFixed(2)}px) rotate(${angleDeg.toFixed(2)}deg)`;
      });
    });
  }

  // Splits one input row's characters across three fixed anchor points instead of letting a single run
  // overflow in place - a plain centered overflow (see the old renderPreviewChars() path) would draw the
  // first overflowing characters directly on top of the stone bezels, since text_area_geometry's box
  // already stops right at their inner edge with no room to spare. side_overflow.leftBoundary/rightBoundary
  // (see personalizer-drawer.liquid) mark each bezel's OUTER edge, so characters that land in the left/right
  // zones start already clear of the stone rather than growing into it. insideMaxChars (customizer.
  // text_area_geometry.side_overflow.insideMaxChars, default 10) is a fixed character-count budget, not a
  // measured fit - text_area_geometry's width/font_combination_settings' fontSize are what get calibrated
  // (against the product photo's actual stone spacing) so that many characters fit exactly.
  renderSplitPreview(middleZone, leftZone, rightZone, text, insideMaxChars) {
    const characters = Array.from(text);
    const overflowCount = Math.max(characters.length - insideMaxChars, 0);
    const leftCount = Math.ceil(overflowCount / 2);

    const leftChars = characters.slice(0, leftCount);
    const middleChars = characters.slice(leftCount, leftCount + insideMaxChars);
    const rightChars = characters.slice(leftCount + insideMaxChars);

    middleZone.classList.remove("sideways", "vertical");
    middleZone.textContent = "";
    middleZone.appendChild(this.buildPreviewLine(middleChars));

    leftZone.textContent = "";
    if (leftChars.length) {
      leftZone.appendChild(this.buildPreviewLine(leftChars));
    }

    rightZone.textContent = "";
    if (rightChars.length) {
      rightZone.appendChild(this.buildPreviewLine(rightChars));
    }
  }

  // Shared by renderPreviewChars() (one line per input row) and renderSplitPreview() (one line per
  // inside/left/right zone) - both just need a `.personalizer-drawer__preview-line` wrapper of char-icon
  // spans, built via the same appendPreviewChar() used everywhere else.
  buildPreviewLine(characters) {
    const lineElement = document.createElement("div");
    lineElement.className = "personalizer-drawer__preview-line";
    characters.forEach((character) => this.appendPreviewChar(lineElement, character));
    return lineElement;
  }

  convertText(text) {
    const unicodeMap = {};

    unicodeMap["&"] = String.fromCharCode(0xe900);

    for (let i = 0; i <= 9; i++) {
        unicodeMap[i] = String.fromCharCode(0xe901 + i);
    }

    for (let i = 0; i < 26; i++) {
        unicodeMap[String.fromCharCode(65 + i)] =
            String.fromCharCode(0xe90b + i);
    }

    unicodeMap["="] = String.fromCharCode(0xe925);
    unicodeMap["+"] = String.fromCharCode(0xe926);
    unicodeMap["%"] = String.fromCharCode(0xe927);
    unicodeMap["."] = String.fromCharCode(0xe928);

    // PERSONALIZER_HEART deliberately has no entry here: confirmed via the actual font files
    // (gothic-lower/gothic-upper/thick-block-stamping-font.woff) that their cmap tables only define glyphs
    // from 0xe900-0xe928 - there is no heart glyph anywhere in any of the three fonts. Mapping it to any
    // codepoint outside that range renders a blank/missing glyph, which is worse than the literal "♥"
    // falling through to the `else` branch below and rendering via the system's fallback heart glyph. A
    // real fix needs an actual heart glyph added to the font files (e.g. at 0xe929) - flag to whoever owns
    // the font asset if a matching engraved-style heart is required here.

    let output = "";

    for (const ch of text.toUpperCase()) {
        if (unicodeMap[ch]) {
            output += unicodeMap[ch];
        } else {
            output += ch;
        }
    }

    return output;
  }

  // Appends one char-icon span directly to the preview text area - no per-line wrapper div - matching the
  // live reference site's flat DOM.
  appendPreviewChar(previewTextArea, character) {
    const charElement = document.createElement("span");
    charElement.className = "char-icon etch";

    if (character === PERSONALIZER_HEART) {
      charElement.classList.add("char-icon--heart");
      charElement.innerHTML = PERSONALIZER_HEART_ICON_SVG;
    } else {
      if (character === " ") {
        charElement.classList.add("char-icon--space");
      }
      charElement.textContent = this.convertText(character);
    }

    previewTextArea.appendChild(charElement);
  }

  // Splits characters into fixed-size chunks - e.g. chunkCharacters(['d','a','d'], 2) -> [['d','a'],['d']] -
  // used to wrap a single row's characters into a grid when customizer.stampings_spec calls for it (see
  // renderPreviewChars()).
  chunkCharacters(characters, size) {
    const chunks = [];
    for (let i = 0; i < characters.length; i += size) {
      chunks.push(characters.slice(i, i + size));
    }
    return chunks;
  }

  // customizer.stampings_spec (parsed into this.stampingsSpec in connectedCallback) is keyed by character
  // count, not by font - e.g. [{"length":3,"orientation":"square",...}] means "when exactly 3 characters
  // are typed, wrap them into a square grid". Re-checked per line, per render, since the count changes as
  // the customer types.
  getStampingSpecEntry(length) {
    return (this.stampingsSpec || []).find((entry) => entry.length === length);
  }

  isSquareLayoutForLength(length) {
    const spec = this.getStampingSpecEntry(length);
    return (spec?.orientation || "").toLowerCase() === "square";
  }

  // customizer.heart_icon_size is keyed by orientation ("sideways"/"vertical"), not by font - mirroring
  // isSquareLayoutForLength()'s per-render lookup, since the orientation can change after the customer has
  // already typed a heart. Falls back to PERSONALIZER_HEART_ICON_DEFAULT_SIZE when the metafield is blank,
  // invalid, or simply doesn't cover the current orientation (it never defines the default horizontal
  // layout, only sideways/vertical).
  getHeartIconSize() {
    const orientation = (this.selectedOrientation || "").toLowerCase();
    const size = this.heartIconSize?.[orientation];
    return typeof size === "number" && size > 0 ? size : PERSONALIZER_HEART_ICON_DEFAULT_SIZE;
  }

  renderPreviewChars(previewTextArea, lines) {
    previewTextArea.textContent = "";

    const orientation = (this.selectedOrientation || "").toLowerCase();
    const isVertical = orientation === "vertical";
    const isSideways = orientation === "sideways";

    // flex-direction/transform for each orientation live in CSS (see .sideways/.vertical on
    // .personalizer-drawer__preview-text-area) - this just toggles which class applies.
    previewTextArea.classList.toggle("sideways", isSideways);
    previewTextArea.classList.toggle("vertical", isVertical);

    // Each input row gets its own wrapper (matching the live site) rather than flattening every row into
    // one run of characters - otherwise two rows typed as e.g. "dadad"/"adada" would render as a single
    // merged 10-character column instead of two separate 5-character ones. Empty rows (not yet typed into)
    // are skipped so they don't render as an empty column taking up layout space.
    // customizer.stampings_spec can further split a row into fixed-size chunks, each getting its own
    // wrapper too - reusing the exact same per-row wrapper mechanism to wrap a single row's characters into
    // a grid (e.g. 3 characters -> a 2-then-1 pyramid) instead of one flat line. Chunk size ("rows") and the
    // gap between the resulting rows ("row_gap") both come from that same metafield entry when present,
    // so merchants can tune a specific character count without a code change - PERSONALIZER_SQUARE_ROW_MAX_CHARS
    // and PERSONALIZER_ROW_GAP_FACTOR are just the fallback when the entry (or that field on it) is missing.
    lines
      .filter((line) => line.length > 0)
      .forEach((line) => {
        const characters = Array.from(line);
        const spec = orientation !== "horizontal" ? this.getStampingSpecEntry(characters.length) : undefined;
        const isSquare = (spec?.orientation || "").toLowerCase() === "square";
        const chunkSize = Number(spec?.rows) > 0 ? Number(spec.rows) : PERSONALIZER_SQUARE_ROW_MAX_CHARS;
        const rowGapFactor = spec?.row_gap !== undefined && Number.isFinite(Number(spec.row_gap))
          ? Number(spec.row_gap)
          : PERSONALIZER_ROW_GAP_FACTOR;
        const rows = isSquare ? this.chunkCharacters(characters, chunkSize) : [characters];

        rows.forEach((rowCharacters) => {
          const lineElement = document.createElement("div");
          lineElement.className = "personalizer-drawer__preview-line";
          lineElement.classList.toggle("vertical", isVertical);
          lineElement.dataset.rowGapFactor = String(rowGapFactor);
          rowCharacters.forEach((character) => this.appendPreviewChar(lineElement, character));
          previewTextArea.appendChild(lineElement);
        });
      });
  }

  // CSS letter-spacing only affects letters within a single text run - it does nothing between separate
  // sibling elements, which is exactly what renderPreviewChars() just built. So spacing between characters
  // has to be a margin on each char-icon span instead (skipped on the last character of each row, matching
  // the live reference site, which only ever showed a margin on all-but-the-last span - each row resets
  // this independently since renderPreviewChars() now gives every input row its own wrapper, rather than
  // treating all rows as one flattened run of characters). "vertical" stacks top-to-bottom, so its spacing
  // is margin-bottom instead of margin-right - "sideways" lays out left-to-right same as the default/
  // horizontal case (it's rotated visually via CSS transform, not via flex-direction), so it uses
  // margin-right too. font-size is set explicitly on every span too - the live reference sets it on both
  // the container and each span rather than relying on inheritance from the parent, so this matches that
  // instead of assuming inheritance holds.
  applyPreviewStyle(previewTextArea, fontSize, letterSpacing) {
    const fontFamily = PERSONALIZER_FONT_FAMILIES[this.selectedFont];
    const orientation = (this.selectedOrientation || "").toLowerCase();
    const isStacked = orientation === "vertical";
    const lineElements = Array.from(previewTextArea.children).filter((el) =>
      el.classList.contains("personalizer-drawer__preview-line")
    );
    if (!isStacked) {
      lineElements.forEach((lineElement, index) => {
        const gapFactor = Number(lineElement.dataset.rowGapFactor);
        const resolvedGapFactor = Number.isFinite(gapFactor) ? gapFactor : PERSONALIZER_ROW_GAP_FACTOR;
        lineElement.style.marginTop = index === 0 ? "0" : `${fontSize * resolvedGapFactor}px`;
      });
    }

    lineElements.forEach((lineElement) => {
      const chars = Array.from(lineElement.children).filter((el) => el.classList.contains("char-icon"));

      chars.forEach((charElement, index) => {
        const isHeart = charElement.classList.contains("char-icon--heart");
        const isSpace = charElement.classList.contains("char-icon--space");

        if (isHeart) {
          // No font glyph (and so no natural advance width) to size the heart by - draw it at the
          // orientation's configured size instead, via an explicit box the SVG fills at 100%.
          const heartSize = this.getHeartIconSize();
          charElement.style.width = `${heartSize}px`;
          charElement.style.height = `${heartSize}px`;
        } else {
          charElement.style.fontSize = `${fontSize}px`;
          if (fontFamily) {
            charElement.style.fontFamily = fontFamily;
          }

          if (isSpace) {
            charElement.style.width = `${fontSize * PERSONALIZER_SPACE_WIDTH_FACTOR}px`;
          }
        }

        const isLast = index === chars.length - 1;
        const nextIsHeart = !isLast && chars[index + 1].classList.contains("char-icon--heart");
        const nextIsSpace = !isLast && chars[index + 1].classList.contains("char-icon--space");
        let spacing = letterSpacing;
        if (isSpace || nextIsSpace) {
          spacing = 0;
        } else if (isHeart || nextIsHeart) {
          spacing = letterSpacing / 2;
        }

        if (isStacked) {
          charElement.style.marginRight = "0";
          charElement.style.marginBottom = isLast ? "0" : `${spacing}px`;
        } else {
          charElement.style.marginBottom = "0";
          charElement.style.marginRight = isLast ? "0" : `${spacing}px`;
        }
      });
    });
  }

  // font_combination_settings' fontSize/spacing are the font's authored values - always used as-is, at
  // 100%, regardless of how much text is typed or how it overflows the fixed-size engraving box.
  // PERSONALIZER_LETTER_SPACING_FACTOR tightens the authored spacing further so characters sit closer
  // together in the preview than the metafield's raw value alone would produce.
  fitPreviewText(previewTextArea) {
    const baseFontSize = parseFloat(previewTextArea.dataset.baseFontSize);
    const baseLetterSpacing = parseFloat(previewTextArea.dataset.baseLetterSpacing || "0") * PERSONALIZER_LETTER_SPACING_FACTOR;

    if (Number.isNaN(baseFontSize) || !previewTextArea.querySelector(".char-icon")) {
      return;
    }

    previewTextArea.style.fontSize = `${baseFontSize}px`;
    this.applyPreviewStyle(previewTextArea, baseFontSize, baseLetterSpacing);
  }

  insertHeart(button) {
    const input = button.closest(".personalizer-drawer__input-row")?.querySelector("[data-personalizer-text-input]");
    if (!input) {
      return;
    }

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    const maxLength = input.maxLength > 0 ? input.maxLength : Infinity;

    if (input.value.length - (end - start) + PERSONALIZER_HEART.length > maxLength) {
      return;
    }

    input.value = input.value.slice(0, start) + PERSONALIZER_HEART + input.value.slice(end);
    input.selectionStart = input.selectionEnd = start + PERSONALIZER_HEART.length;
    this.updateCharCounter(input);
    this.refreshPreviewFromInputs();
    input.focus();
  }
}

if (!window.customElements.get("personalizer-steps")) {
  window.customElements.define("personalizer-steps", PersonalizerSteps);
}